import { FunctionComponent, useState, useEffect } from "react";
import desktopStyles from "./styles/NotificationDesktopItem.module.css";
import mobileStyles from "./styles/NotificationMobileItem.module.css";
import NotificationStatusIcon from "../NotificationStatusIcon";
import {
  LabelPairedCheckMdRegularIcon,
  LabelPairedTrashSmRegularIcon,
} from "@deriv/quill-icons";
import { useHorizontalSwipe } from "../../../hooks/useHorizontalSwipe";
import { useDevice } from "../../../hooks/useDevice";
import { useFocusHandling } from "../../../hooks/useFocusHandling";

export type NotificationItemType = {
  className?: string;
  date?: string;
  time?: string;
  title?: string;
  message?: string;
  timeFormat?: string;
  showDivider?: boolean;
  showFocusRing?: boolean;

  /** Variant props */
  variant?: "desktop" | "mobile" | "auto";
  state?: "default" | "swipe";
  type?: "informative" | "success" | "warning" | "danger";

  /** Action handlers */
  onMarkAsRead?: () => void;
  onDelete?: () => void;
};

export const NotificationItem: FunctionComponent<NotificationItemType> = ({
  className = "",
  variant = "auto",
  state = "default",
  date = "1 Jan 2023",
  time = "00:00",
  title = "Title",
  message = "Message goes here",
  timeFormat = "UTC",
  showDivider = false,
  showFocusRing = false,
  type = "informative",
  onMarkAsRead,
  onDelete,
}) => {
  // Determine which variant to use based on device if set to auto
  const { isMobile } = useDevice();
  const currentVariant =
    variant === "auto" ? (isMobile ? "mobile" : "desktop") : variant;

  // Select the appropriate styles based on variant
  const styles = currentVariant === "desktop" ? desktopStyles : mobileStyles;

  // Generate a unique ID for the component
  const notificationId = `notification-${currentVariant}-item-${type}`;

  // Mobile-specific swipe handling
  const { isSwiped, resetSwipe, swipeHandlers } = useHorizontalSwipe({
    initialState: state === "swipe",
    threshold: 30,
  });

  const displayState = isSwiped ? "swipe" : "default";

  // Track if the notification has been clicked/read
  const [isRead, setIsRead] = useState(false);

  // Track focus state for keyboard navigation
  const [focus, setFocus] = useState(showFocusRing);

  // Handle focus events
  const { handleFocus, handleBlur } = useFocusHandling({
    isDisabled: false,
    setHighlight: setFocus,
  });

  // Reset isRead state when state prop changes (for Storybook controls)
  useEffect(() => {
    if (state === "default") {
      setIsRead(false);
    }
  }, [state]);

  // Handle notification click or mark as read
  const handleNotificationClick = (readButton = false) => {
    setIsRead(true);
    if (onMarkAsRead) {
      // Call onMarkAsRead if it's from the button or not in swipe state
      if (readButton || displayState !== "swipe") {
        onMarkAsRead();
      }
    }
    // Reset swipe if action came from button
    if (readButton && currentVariant === "mobile") {
      resetSwipe();
    }
  };

  // Handle item click
  const handleItemClick = () => {
    handleNotificationClick(false);
  };

  // Render desktop variant
  if (currentVariant === "desktop") {
    return (
      <div
        className={[
          styles.root,
          className,
          "quill-ui-next-pointer",
          "quill-ui-next-component",
        ].join(" ")}
        data-state={state}
        data-id={notificationId}
        onClick={handleItemClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={0}
      >
        <div className={styles.containerStatusbadge}>
          <NotificationStatusIcon type={type} showBadge={!isRead} />
        </div>
        <div className={styles.detailsActionButtons}>
          <div className={styles.details}>
            <div className={styles.titleDescription}>
              <b className={styles.title}>{title}</b>
              <div className={styles.message}>{message}</div>
            </div>
            <div className={styles.dateTime}>
              <div className={styles.date}>{date}</div>
              <div className={styles.timeTimeFormat}>
                <div className={styles.time}>{time}</div>
                <div className={styles.timeFormat}>{timeFormat}</div>
              </div>
            </div>
          </div>
          <div className={styles.containerActionButtons}>
            <div
              className={styles.buttonMarkAsRead}
              onClick={() => handleNotificationClick(true)}
              role={onMarkAsRead ? "button" : undefined}
              tabIndex={onMarkAsRead ? 0 : undefined}
            >
              <div className={`${styles.icon2} action-button-icon`}>
                <LabelPairedCheckMdRegularIcon />
              </div>
              <div className={styles.bgFocus} />
            </div>
            <div
              className={styles.buttonDelete}
              onClick={() => onDelete?.()}
              role={onDelete ? "button" : undefined}
              tabIndex={onDelete ? 0 : undefined}
            >
              <div className={`${styles.icon4} action-button-icon`}>
                <LabelPairedTrashSmRegularIcon />
              </div>
              <div className={styles.bgFocus1} />
            </div>
          </div>
        </div>
        {showDivider && <div className={styles.divider} />}
        {focus && <div className={styles.bgFocus2} />}
      </div>
    );
  }

  // Render mobile variant
  return (
    <div
      className={[styles.root, className, "quill-ui-next-pointer"].join(" ")}
      data-state={displayState}
      data-id={notificationId}
      {...swipeHandlers}
    >
      <div className={styles.item} onClick={handleItemClick}>
        <div className={styles.containerStatusbadge}>
          <NotificationStatusIcon type={type} showBadge={!isRead} />
        </div>
        <div className={styles.details}>
          <div className={styles.titleDescription}>
            <b className={styles.title}>{title}</b>
            <div className={styles.message}>{message}</div>
          </div>
          <div className={styles.dateTime}>
            <div className={styles.date}>{date}</div>
            <div className={styles.timeTimeFormat}>
              <div className={styles.time}>{time}</div>
              <div className={styles.timeFormat}>{timeFormat}</div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${styles.buttonMarkAsRead} quill-ui-next-pointer`}
        onClick={() => {
          if (displayState === "swipe") {
            handleNotificationClick(true);
          }
        }}
        role={onMarkAsRead && displayState === "swipe" ? "button" : undefined}
        tabIndex={onMarkAsRead && displayState === "swipe" ? 0 : undefined}
      >
        <div className="action-button-icon">
          <LabelPairedCheckMdRegularIcon />
        </div>
      </div>
      <div
        className={`${styles.buttonDelete} quill-ui-next-pointer`}
        onClick={() => {
          if (displayState === "swipe" && onDelete) {
            onDelete();
            resetSwipe();
          }
        }}
        role={onDelete && displayState === "swipe" ? "button" : undefined}
        tabIndex={onDelete && displayState === "swipe" ? 0 : undefined}
      >
        <div className="action-button-icon">
          <LabelPairedTrashSmRegularIcon />
        </div>
      </div>
      {showDivider && <div className={styles.divider} />}
    </div>
  );
};

export default NotificationItem;
